import Button from "@mui/material/Button";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import * as React from "react";
import axiosInstance from "../../axiosInstance";

const ConfigIntegrations = (props: { siteId: string | undefined }) => {
  // --- Developer Token state ---
  const [token, setToken] = React.useState<string>("");
  const [refreshToken, setRefreshToken] = React.useState<string>("");
  const [tokenLoading, setTokenLoading] = React.useState(false);
  const [tokenCopied, setTokenCopied] = React.useState(false);
  const [refreshTokenCopied, setRefreshTokenCopied] = React.useState(false);
  const [tokenRes, setTokenRes] = React.useState({
    message: "",
    isVisible: false,
    isError: false,
  });

  // --- MCP Connected Apps state ---
  const [mcpApps, setMcpApps] = React.useState<any[]>([]);
  const [mcpRevokeRes, setMcpRevokeRes] = React.useState({
    message: "",
    isVisible: false,
    isError: false,
  });

  async function generateToken() {
    setTokenLoading(true);
    try {
      const response = await axiosInstance.post("oauth/developer-token", {
        siteId: props.siteId,
      });
      setToken(response.data.token || response.data.access_token || "");
      setRefreshToken(response.data.refresh_token || response.data.refreshToken || "");
      setTokenRes({
        message: "Tokens generated successfully. Copy them now — they won't be shown again.",
        isVisible: true,
        isError: false,
      });
    } catch (err: any) {
      setTokenRes({
        message: err.response?.data?.message || "Failed to generate token",
        isVisible: true,
        isError: true,
      });
    } finally {
      setTokenLoading(false);
      setTimeout(() => {
        setTokenRes((old) => ({ ...old, isVisible: false }));
      }, 10000);
    }
  }

  async function copyToClipboard(
    text: string,
    setCopied: React.Dispatch<React.SetStateAction<boolean>>
  ) {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function fetchMcpApps() {
    try {
      const response = await axiosInstance.get("/api/mcp-connected-apps");
      setMcpApps(response.data.apps || []);
    } catch (err) {
      console.log("fetchMcpApps error", err);
    }
  }

  async function revokeMcpApp(clientId: string) {
    try {
      const response = await axiosInstance.post("/api/revoke-mcp-app", {
        clientId,
      });
      setMcpRevokeRes({
        message: response.data.message,
        isVisible: true,
        isError: false,
      });
      fetchMcpApps();
    } catch (err: any) {
      setMcpRevokeRes({
        message: err.response?.data?.message || "Failed to revoke",
        isVisible: true,
        isError: true,
      });
    } finally {
      setTimeout(() => {
        setMcpRevokeRes((old) => ({ ...old, isVisible: false }));
      }, 5000);
    }
  }

  React.useEffect(() => {
    fetchMcpApps();
  }, []);

  return (
    <div className="flex flex-col text-black h-screen flex-1 md:mr-4 mt-2.5 md:mt-0">
      {/* Developer Token Section */}
      <div className="developer-token mb-8">
        <label
          style={{
            color: "#243060",
            marginBottom: "12px",
            display: "flex",
          }}
        >
          Developer Token
        </label>
        <p
          className="font-normal text-sm mb-4"
          style={{ color: "#3B3B3B" }}
        >
          Generate an access token to integrate with external services and APIs.
        </p>

        {token && (
          <div className="mb-3">
            <label className="text-sm font-semibold" style={{ color: "#3B3B3B" }}>
              Access Token
            </label>
            <div className="flex items-center gap-2 p-3 bg-gray-100 rounded border border-gray-300 mt-1">
              <code
                className="flex-1 text-sm break-all"
                style={{ color: "#243060" }}
              >
                {token}
              </code>
              <Button
                size="small"
                onClick={() => copyToClipboard(token, setTokenCopied)}
                sx={{
                  minWidth: "auto",
                  padding: "4px 8px",
                  color: "#243060",
                  textTransform: "none",
                }}
                startIcon={<ContentCopyRoundedIcon fontSize="small" />}
              >
                {tokenCopied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
        )}

        {refreshToken && (
          <div className="mb-3">
            <label className="text-sm font-semibold" style={{ color: "#3B3B3B" }}>
              Refresh Token
            </label>
            <div className="flex items-center gap-2 p-3 bg-gray-100 rounded border border-gray-300 mt-1">
              <code
                className="flex-1 text-sm break-all"
                style={{ color: "#243060" }}
              >
                {refreshToken}
              </code>
              <Button
                size="small"
                onClick={() => copyToClipboard(refreshToken, setRefreshTokenCopied)}
                sx={{
                  minWidth: "auto",
                  padding: "4px 8px",
                  color: "#243060",
                  textTransform: "none",
                }}
                startIcon={<ContentCopyRoundedIcon fontSize="small" />}
              >
                {refreshTokenCopied ? "Copied!" : "Copy"}
              </Button>
            </div>
          </div>
        )}

        {tokenRes.isVisible && (
          <div className="mb-3">
            <span
              className={`text-sm ${
                tokenRes.isError ? "text-red-500" : "text-green-500"
              }`}
            >
              {tokenRes.message}
            </span>
          </div>
        )}

        <Button
          variant="contained"
          size="small"
          disabled={tokenLoading}
          onClick={generateToken}
          sx={{
            backgroundColor: "#243060",
            border: "1px solid",
            borderColor: "#243060",
            color: "#fff",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#243060",
              color: "#fff",
            },
          }}
        >
          {tokenLoading ? "Generating..." : "Generate Token"}
        </Button>
      </div>

      {/* MCP Connected Apps Section */}
      <div className="pt-5 border-t border-slate-300 mcp-connected-apps">
        <label
          style={{
            color: "#243060",
            marginBottom: "24px",
            display: "flex",
          }}
        >
          Connected Apps & Tokens
        </label>

        {mcpRevokeRes.isVisible && (
          <div className="mb-3">
            <span
              className={`text-sm ${
                mcpRevokeRes.isError
                  ? "text-red-500"
                  : "text-green-500"
              }`}
            >
              {mcpRevokeRes.message}
            </span>
          </div>
        )}

        {mcpApps.length === 0 ? (
          <p
            className="font-normal text-sm"
            style={{ color: "#3B3B3B" }}
          >
            No apps or tokens are currently connected to your account.
          </p>
        ) : (
          mcpApps.map((app) => (
            <div
              key={app.clientId}
              className="flex flex-col items-start justify-between sm:flex-row sm:items-center mb-4"
            >
              <div className="flex flex-col">
                <label style={{ fontWeight: 600, color: "#3B3B3B" }}>
                  {app.clientName}
                </label>
                <p
                  style={{ color: "#3B3B3B" }}
                  className="mb-0 font-normal text-sm"
                >
                  Grants full access to RocketPages APIs
                </p>
                <p
                  className="mb-0 font-normal text-sm mt-1"
                  style={{ color: "#6B7280" }}
                >
                  Connected on{" "}
                  {new Date(app.connectedAt).toLocaleDateString()}{" "}
                  at {new Date(app.connectedAt).toLocaleTimeString()}
                </p>
              </div>
              <div className="flex ml-0 mt-2 sm:ml-0 sm:mt-0">
                <Button
                  className="px-4 py-2"
                  variant="contained"
                  size="small"
                  onClick={() => revokeMcpApp(app.clientId)}
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid",
                    borderColor: "#ff0000",
                    color: "#ff0000",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#ff0000",
                      color: "#fff",
                    },
                  }}
                >
                  Revoke
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConfigIntegrations;
